import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import catalog from '../../data/buildconstruxCatalog';
import InternalBackLink from '../Internal/InternalBackLink';
import useInternalTheme from '../../hooks/useInternalTheme';
import '../Internal/internalXlabsChrome.css';
import './BuildconstruxPortfolio.css';

const CATEGORY_LABELS = {
  'assembly-guides': 'Assembly guides',
  'brand-logo': 'Brand',
  'factory-shop': 'Factory',
  'finished-structure': 'Finished',
  'jobsite-foundation': 'Foundation',
  'jobsite-framing': 'Framing',
  'marketing-graphics': 'Marketing',
  'panel-assembly': 'Panel assembly',
  'people-spokesperson': 'People',
  'press-events': 'Press',
  'product-renders': 'Product renders',
  'site-context': 'Site context',
  'stock-broll': 'Stock b-roll',
};

const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

function mediaUrl(assetId, filename) {
  if (!filename) return '';
  return `${catalog.mediaBase}/${assetId}/${encodeURIComponent(filename)}`;
}

function formatLabel(format) {
  if (format === 'video') return 'Video';
  if (format === 'gallery') return 'Gallery';
  return 'Still';
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 001.5.86l11-6.86a1 1 0 000-1.72l-11-6.86a1 1 0 00-1.5.86z" />
    </svg>
  );
}

function Chevron({ dir }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
      {dir === 'left' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
    </svg>
  );
}

function PortfolioLightbox({ asset, onClose, onPrev, onNext, hasPrev, hasNext }) {
  const [frame, setFrame] = useState(0);
  const [closing, setClosing] = useState(false);
  const closeRef = useRef(null);
  const touchStart = useRef(null);

  const isGallery = asset.format === 'gallery';
  const isVideo = asset.format === 'video';
  const frames = asset.media || [];
  const activeFile = frames[Math.min(frame, frames.length - 1)];

  const requestClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(onClose, 280);
  }, [closing, onClose]);

  useEffect(() => {
    setFrame(0);
  }, [asset.id]);

  useEffect(() => {
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') requestClose();
      if (e.key === 'ArrowLeft') {
        if (isGallery && frame > 0) setFrame((f) => f - 1);
        else if (hasPrev) onPrev();
      }
      if (e.key === 'ArrowRight') {
        if (isGallery && frame < frames.length - 1) setFrame((f) => f + 1);
        else if (hasNext) onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [requestClose, onPrev, onNext, hasPrev, hasNext, isGallery, frame, frames.length]);

  const onTouchStart = (e) => {
    touchStart.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 56) {
      if (delta > 0) {
        if (isGallery && frame > 0) setFrame((f) => f - 1);
        else if (hasPrev) onPrev();
      } else if (isGallery && frame < frames.length - 1) setFrame((f) => f + 1);
      else if (hasNext) onNext();
    }
    touchStart.current = null;
  };

  return createPortal(
    <div className={`bc-port-lb${closing ? ' is-closing' : ''}`} role="dialog" aria-modal="true" aria-label={asset.description}>
      <button type="button" className="bc-port-lb__backdrop" aria-label="Close" onClick={requestClose} />
      <div className="bc-port-lb__stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <header className="bc-port-lb__bar">
          <div className="bc-port-lb__meta">
            <span className="bc-port-lb__cat">{CATEGORY_LABELS[asset.category] || asset.category}</span>
            <h2>{asset.description}</h2>
            <p>
              <code>{asset.id}</code>
              <span aria-hidden="true"> · </span>
              {formatLabel(asset.format)}
              <span aria-hidden="true"> · </span>
              {asset.orientation}
              {isGallery ? ` · ${frame + 1}/${frames.length}` : null}
            </p>
          </div>
          <button ref={closeRef} type="button" className="bc-port-lb__close" onClick={requestClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        <div className={`bc-port-lb__media is-${asset.orientation}`}>
          {isVideo ? (
            <video key={asset.id} src={mediaUrl(asset.id, activeFile)} controls autoPlay playsInline poster={mediaUrl(asset.id, asset.thumb)} />
          ) : (
            <img key={`${asset.id}-${activeFile}`} src={mediaUrl(asset.id, activeFile)} alt={asset.description} />
          )}
        </div>

        <div className="bc-port-lb__nav">
          <button type="button" disabled={!hasPrev && !(isGallery && frame > 0)} onClick={() => (isGallery && frame > 0 ? setFrame((f) => f - 1) : onPrev())} aria-label="Previous">
            <Chevron dir="left" />
          </button>
          <button type="button" disabled={!hasNext && !(isGallery && frame < frames.length - 1)} onClick={() => (isGallery && frame < frames.length - 1 ? setFrame((f) => f + 1) : onNext())} aria-label="Next">
            <Chevron dir="right" />
          </button>
        </div>

        {isGallery && frames.length > 1 ? (
          <div className="bc-port-lb__thumbs" role="list">
            {frames.map((file, i) => (
              <button
                key={file}
                type="button"
                role="listitem"
                className={i === frame ? 'is-active' : undefined}
                onClick={() => setFrame(i)}
                aria-label={`Frame ${i + 1}`}
              >
                <img src={mediaUrl(asset.id, file)} alt="" />
              </button>
            ))}
          </div>
        ) : null}

        {asset.tags?.length ? (
          <ul className="bc-port-lb__tags">
            {asset.tags.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

function AssetTile({ asset, index, onOpen }) {
  const thumb = mediaUrl(asset.id, asset.thumb);
  return (
    <button
      type="button"
      className={`bc-port-tile is-${asset.orientation} is-${asset.format}`}
      style={{ '--delay': `${Math.min(index, 24) * 28}ms` }}
      onClick={() => onOpen(asset.id)}
    >
      <span className="bc-port-tile__media">
        <img src={thumb} alt="" loading="lazy" />
        {asset.format === 'video' ? (
          <span className="bc-port-tile__play" aria-hidden="true">
            <PlayIcon />
          </span>
        ) : null}
      </span>
      <span className="bc-port-tile__caption">
        <span className="bc-port-tile__badge">{formatLabel(asset.format)}</span>
        <span className="bc-port-tile__title">{asset.description}</span>
        <span className="bc-port-tile__slug">{CATEGORY_LABELS[asset.category]}</span>
      </span>
    </button>
  );
}

export default function BuildconstruxPortfolio() {
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(null);

  const assets = catalog.assets;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return assets.filter((a) => {
      if (category !== 'all' && a.category !== category) return false;
      if (!q) return true;
      const hay = `${a.slug} ${a.description} ${a.id} ${(a.tags || []).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [assets, category, query]);

  const activeIndex = filtered.findIndex((a) => a.id === activeId);
  const activeAsset = activeIndex >= 0 ? filtered[activeIndex] : null;

  const categoryCounts = useMemo(() => {
    const counts = { all: assets.length };
    for (const a of assets) counts[a.category] = (counts[a.category] || 0) + 1;
    return counts;
  }, [assets]);

  const theme = useInternalTheme();

  return (
    <div
      className="bc-port xlabs-chrome"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>media · internal</title>
        <meta name="robots" content="noindex,nofollow" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <header className="bc-port__hero">
        <p className="xc-eyebrow">internal · media</p>
        <h1>Media library</h1>
        <p>
          {catalog.asset_count} archived Webflow assets — framing, factory, press, and product renders from the legacy
          buildconstrux site.
        </p>
        <div className="bc-port__hero-meta">
          <InternalBackLink className="xc-back" />
          <span>{Object.keys(catalog.categories).length} categories</span>
        </div>
      </header>

      <div className="bc-port__toolbar">
        <label className="bc-port__search">
          <span className="sr-only">Search</span>
          <input
            type="search"
            placeholder="Search slug, tag, id…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="bc-port__chips" role="tablist" aria-label="Categories">
          <button
            type="button"
            role="tab"
            aria-selected={category === 'all'}
            className={category === 'all' ? 'is-active' : undefined}
            onClick={() => setCategory('all')}
          >
            All <em>{categoryCounts.all}</em>
          </button>
          {CATEGORY_ORDER.map((key) =>
            categoryCounts[key] ? (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={category === key}
                className={category === key ? 'is-active' : undefined}
                onClick={() => setCategory(key)}
              >
                {CATEGORY_LABELS[key]} <em>{categoryCounts[key]}</em>
              </button>
            ) : null
          )}
        </div>
      </div>

      <p className="bc-port__count" aria-live="polite">
        Showing {filtered.length} of {assets.length}
      </p>

      {filtered.length === 0 ? (
        <p className="bc-port__empty">No assets match. Clear search or pick another category.</p>
      ) : (
        <div className="bc-port__grid">
          {filtered.map((asset, i) => (
            <AssetTile key={asset.id} asset={asset} index={i} onOpen={setActiveId} />
          ))}
        </div>
      )}

      {activeAsset ? (
        <PortfolioLightbox
          asset={activeAsset}
          onClose={() => setActiveId(null)}
          hasPrev={activeIndex > 0}
          hasNext={activeIndex < filtered.length - 1}
          onPrev={() => setActiveId(filtered[activeIndex - 1].id)}
          onNext={() => setActiveId(filtered[activeIndex + 1].id)}
        />
      ) : null}
    </div>
  );
}
